import React, { useState, useEffect } from "react";
import { useCompany } from "../../../contexts/CompanyContext";
import { costCentersApi } from "../../../services";
import type { CostCenterAttributes } from "../../../types";
import {
  ConfirmationModal,
  Card,
  EmptyState,
  Loading,
  ErrorState,
  IconsLarge,
} from "../../shared";
import { AdminPageHeader } from "../shared";
import { CreateCostCenterModal } from "./CreateCostCenterModal";
import { CostCentersListView } from "./CostCentersListView";

export const CostCenter: React.FC = () => {
  const { company } = useCompany();
  const companyId = company?.id ?? 0;
  const [costCenters, setCostCenters] = useState<CostCenterAttributes[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCostCenter, setEditingCostCenter] =
    useState<CostCenterAttributes | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [costCenterToDelete, setCostCenterToDelete] = useState<number | null>(
    null
  );
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchCostCenters = async () => {
    if (companyId === 0) {
      setCostCenters([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await costCentersApi.getAll(companyId);
      if (response.success && response.data) {
        setCostCenters(response.data);
      } else {
        setError(
          response.error ?? "Falha ao carregar centros de custo"
        );
      }
    } catch {
      setError("Falha ao carregar centros de custo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCostCenters();
  }, [companyId]);

  const handleEdit = (costCenter: CostCenterAttributes) => {
    setEditingCostCenter(costCenter);
    setShowCreateForm(true);
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingCostCenter(null);
  };

  const handleSaved = () => {
    fetchCostCenters();
    setShowCreateForm(false);
    setEditingCostCenter(null);
  };

  const handleDelete = (id: number) => {
    setCostCenterToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!costCenterToDelete) return;
    setDeleteLoading(true);
    setError(null);
    try {
      const response = await costCentersApi.delete(costCenterToDelete);
      if (response.success) {
        await fetchCostCenters();
        setShowDeleteModal(false);
        setCostCenterToDelete(null);
      } else {
        setError(
          response.error ?? "Falha ao excluir centro de custo"
        );
      }
    } catch {
      setError("Falha ao excluir centro de custo");
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setCostCenterToDelete(null);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <AdminPageHeader
        icon={<IconsLarge.CostCenter />}
        title="Centros de Custo"
        subtitle="Gerencie os centros de custo da empresa"
        buttonText="Novo centro de custo"
        onButtonClick={() => {
          setEditingCostCenter(null);
          setShowCreateForm(true);
        }}
        buttonDisabled={loading}
        showViewToggle={false}
      />

      {error && (
        <ErrorState
          message={error}
          variant="card"
          onRetry={() => fetchCostCenters()}
        />
      )}

      <CreateCostCenterModal
        isOpen={showCreateForm}
        onClose={handleCancel}
        onSuccess={handleSaved}
        companyId={companyId}
        editingCostCenter={editingCostCenter}
      />

      {loading && !showCreateForm ? (
        <Loading
          message="Carregando centros de custo..."
          size="xl"
          className="bg-white rounded-2xl shadow-medium border border-gray-100 p-12"
        />
      ) : (
        <Card className="p-8">
          {costCenters.length === 0 ? (
            <EmptyState
              icon={<IconsLarge.CostCenter />}
              title="Nenhum centro de custo encontrado"
              description="Cadastre o primeiro centro de custo para começar."
              actionText="Novo centro de custo"
              onAction={() => setShowCreateForm(true)}
            />
          ) : (
            <CostCentersListView
              costCenters={costCenters}
              onEdit={handleEdit}
              onDelete={handleDelete}
              loading={loading}
            />
          )}
        </Card>
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir centro de custo"
        message="Tem certeza que deseja excluir este centro de custo? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        loading={deleteLoading}
      />
    </div>
  );
};

export default CostCenter;
